import { styled, Box, Button } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArchiveCampaignMutation,
  ArchiveCampaignMutationVariables,
  CampaignStatus,
  CopyCampaignMutation,
  CopyCampaignMutationVariables,
  UnarchiveCampaignMutation,
  UnarchiveCampaignMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  COPY_CAMPAIGN,
  ARCHIVE_CAMPAIGN,
  UNARCHIVE_CAMPAIGN,
} from "~frontend/graphql/campaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import {
  confirmMessageArchive,
  confirmMessageUnarchive,
} from "~frontend/util/campaignActionConfirmMessages";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../types";

const ActionButtonWrapper = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  marginBottom: "12px",
});

const ActionButton = styled(Button)({
  marginLeft: "12px",
});

export default function TopActionButtons({
  campaign,
}: {
  campaign: CampaignForEditor;
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [copyCampaign] = useMutation<
    CopyCampaignMutation,
    CopyCampaignMutationVariables
  >(COPY_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not copy campaign",
    refetchQueries: ["listCampaigns"],
  });

  const [archiveCampaign] = useMutation<
    ArchiveCampaignMutation,
    ArchiveCampaignMutationVariables
  >(ARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not archive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  const [unarchiveCampaign] = useMutation<
    UnarchiveCampaignMutation,
    UnarchiveCampaignMutationVariables
  >(UNARCHIVE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not unarchive campaign",
    refetchQueries: ["listCampaigns"],
    awaitRefetchQueries: true,
  });

  async function handleCopy() {
    const { data, errors } = await copyCampaign({
      variables: {
        input: {
          campaignId: campaign.id,
        },
      },
    });

    if (!errors) {
      navigate(
        subaccountLink(`/campaigns/${data!.copyCampaign.shortId}/edit/basics`)
      )?.catch(console.error);
    }
  }

  async function handleArchive() {
    try {
      await confirm({
        title: (
          <>
            Archive <strong>{campaign.name}</strong>
          </>
        ),
        content: confirmMessageArchive(campaign),
      });
    } catch {
      // user canceled
      return;
    }

    await archiveCampaign({
      variables: { input: { campaignId: campaign.id } },
    });
  }

  async function handleUnarchive() {
    try {
      await confirm({
        title: "Unarchive Campaign",
        content: confirmMessageUnarchive(campaign),
      });
    } catch {
      // user canceled
      return;
    }

    await unarchiveCampaign({
      variables: { input: { campaignId: campaign.id } },
    });
  }

  return (
    <ActionButtonWrapper>
      {campaign.startedAt != null && (
        <ActionButton
          variant="contained"
          color="accessory"
          onClick={() =>
            navigate(subaccountLink(`/campaigns/${campaign.shortId}`))
          }
        >
          View Campaign
        </ActionButton>
      )}
      {campaign.status === CampaignStatus.Archived ? (
        <ActionButton
          variant="contained"
          color="accessory"
          onClick={handleUnarchive}
        >
          Unarchive
        </ActionButton>
      ) : (
        <ActionButton
          variant="contained"
          color="accessory"
          onClick={handleArchive}
        >
          Archive
        </ActionButton>
      )}
      <ActionButton variant="contained" color="accessory" onClick={handleCopy}>
        Copy
      </ActionButton>
    </ActionButtonWrapper>
  );
}
